<template>
  <div>
    <v-my-card>
      <v-card-title>
        <h1>{{ machine.name }} {{ $t("general.dashboards.dashboard") }}</h1>
      </v-card-title>
      <div>
        <v-container fluid>
          <v-layout
            row
            justify-center
            wrap
            class="chart-wrapper"
          >
            <div
              class="grid"
              :class="responsiveClass"
            >
              <v-select
                v-model="activeKeys"
                multiple
                :items="keys"
                item-text="label"
                item-value="queryKey"
                :label="$t('general.dashboards.chart.parameter_filter')"
                class="parameter-select"
                @change="loadAllValues"
              />
              <v-select
                v-model="activeResolution"
                :items="availableResolutions"
                item-text="label"
                item-value="value"
                :label="$t('general.dashboards.chart.resolution')"
                @change="loadAllValues"
              />
              <div>
                <v-datetime-picker
                  v-model="startDateTime"
                  :label="$t('general.dashboards.chart.start_time')"
                  @input="loadAllValues"
                />
              </div>
              <div>
                <v-datetime-picker
                  v-model="endDateTime"
                  :label="$t('general.dashboards.chart.end_time')"
                  @input="loadAllValues"
                />
              </div>
              <v-btn
                @click="endNow"
              >
                {{ $t('general.dashboards.chart.end_now') }}
              </v-btn>
            </div>
            <v-flex
              v-if="!hasEmptyData"
              xs12
              d-flex
              align-center
              class="chart-container"
            >
              <div class="text-xs-center">
                <v-progress-circular
                  v-if="!loaded"
                  indeterminate
                  color="#193b95"
                />
              </div>
              <v-chart
                v-if="loaded"
                :key="machine.id"
                :chartdata="chartData"
                :time="timeSpan"
              />
            </v-flex>
            <h2
              v-else
              class="empty-data"
            >
              {{ $t('general.dashboards.emptyDataset') }}
            </h2>
          </v-layout>
        </v-container>
      </div>
    </v-my-card>

    <v-latest-data
      :latest-data="latestData"
      :keys="keys"
    />

    <div>
      <v-container
        fluid
        grid-list-xl
        class="pump-list"
      >
        <v-layout
          row
          wrap
        >
          <v-flex
            v-for="n in 4"
            :key="n"
            sm6
          >
            <v-device
              :collection-id="(n).toString()"
              :machine-key="machine.key"
            />
          </v-flex>
        </v-layout>
      </v-container>
    </div>
  </div>
</template>

<script>
import VChart from '@/components/Chart.vue';
import VDevice from '@/components/Device.vue';
import VMyCard from '@/components/Card.vue';
import VLatestData from '@/components/LatestData.vue';
import gql from 'graphql-tag';
import { DateTime } from 'luxon';

const GET_MEAN_VALUES_QUERY = gql`
  query getMeanValues(
    $key: String!
    $host: String!
    $topic: String!
    $startTime: String!
    $endTime: String!
    $resolution: String
  ) {
    getMeanValues(key: $key, host: $host, startTime: $startTime, endTime: $endTime, topic: $topic, resolution: $resolution) {
      time
      value
    }
  }
`;

const GET_LATEST_VALUES_QUERY = gql`
  query getLatestValues(
    $key: String!
    $host: String!
    $topic: String!
  ) {
    getLatestValues(
      key: $key,
      host: $host,
      topic: $topic,
      collectionId: "0",
    ) {
      time
      value
    }
  }
`;

function format(n, digits) {
  const factor = 10 ** digits;
  return (Math.round(n * factor) / factor).toFixed(digits);
}

export default {
  name: 'Machine',

  components: {
    VChart,
    VDevice,
    VMyCard,
    VLatestData,
  },

  props: {
    machine: {
      default: () => {},
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      startDateTime: DateTime.now().minus({ day: 1 }).toJSDate(),
      endDateTime: DateTime.now().toJSDate(),
      activeKeys: [],
      queryData: {},
      latestData: {},
      keys: [
        {
          queryKey: 'Values_Leitfähigkeit',
          label: 'Leitfähigkeit',
          color: 'red',
          measure: 'mS/cm',
          formatter: n => format(n, 0),
        },
        {
          queryKey: 'Values_Wasseruhr',
          label: 'Wasseruhr',
          color: 'green',
          measure: 'm³',
          formatter: n => format(n, 2),
        },
        {
          queryKey: 'Values_VolumenstromV1',
          label: 'Volumenstrom',
          color: 'orange',
          measure: 'L/h',
          formatter: n => format(n, 2),
        },
        {
          queryKey: 'Values_Gehäusetemperatur',
          label: 'Gehäusetemperatur',
          color: 'blue',
          measure: '°C',
          formatter: n => format(n, 1),
        },
      ],
      loaded: false,
      timeSpan: this.$t('general.dashboards.chart.one_day'),
      timeOptions: [
        this.$t('general.dashboards.chart.one_day'),
        this.$t('general.dashboards.chart.one_week'),
        this.$t('general.dashboards.chart.one_month'),
      ],
      availableResolutions: [
        {
          value: '1d',
          label: this.$t('general.dashboards.chart.one_day'),
        },
        {
          value: '6h',
          label: this.$t('general.dashboards.chart.six_hours'),
        },
        {
          value: '10m',
          label: this.$t('general.dashboards.chart.ten_minutes'),
        },
        {
          value: '5m',
          label: this.$t('general.dashboards.chart.five_minutes'),
        },
        {
          value: '1m',
          label: this.$t('general.dashboards.chart.one_minute'),
        },
        {
          value: '10s',
          label: this.$t('general.dashboards.chart.ten_seconds'),
        },
      ],
      activeResolution: null,
    };
  },

  computed: {
    responsiveClass() {
      if (this.$vuetify.breakpoint.smAndDown) return 'smAndDown';
      return 'mdAndUp';
    },
    chartData() {
      const allDatasets = Object.values(this.queryData);
      if (allDatasets.length > 0) {
        return {
          labels: allDatasets[0].map(value => DateTime.fromISO(value.time)),
          datasets: this.activeKeys.map((activeKey) => {
            const matchingKey = this.keys.find(key => key.queryKey === activeKey);
            return {
              data: this.queryData[activeKey].map(value => value.value),
              label: `${matchingKey.label} (${matchingKey.measure})`,
              borderColor: matchingKey.color,
              fill: false,
              customFormatter: matchingKey.formatter,
            };
          }),
        };
      }
      return {};
    },
    serializedTimeSpan() {
      switch (this.timeSpan) {
        case this.$t('general.dashboards.chart.one_day'):
          return '7d';
        case this.$t('general.dashboards.chart.one_week'):
          return '1w';
        case this.$t('general.dashboards.chart.one_month'):
          return '30d';
        default:
          return '30d';
      }
    },
    hasEmptyData() {
      const allDatasets = Object.values(this.queryData);
      if (allDatasets.length > 0) {
        return allDatasets[0].length === 0;
      }
      return true;
    },
  },

  async created() {
    this.activeKeys = this.keys.map(keyObject => keyObject.queryKey);
    await this.loadAllValues();
  },

  methods: {
    async loadAllValues() {
      const queryVariables = {
        host: 'gobblespit-oauth-pierre',
        startTime: DateTime.fromJSDate(this.startDateTime).toISO(),
        endTime: DateTime.fromJSDate(this.endDateTime).toISO(),
        topic: this.machine.key,
        resolution: this.activeResolution,
      };

      try {
        this.loaded = false;

        await Promise.all(
          this.keys.filter(key => this.activeKeys.includes(key.queryKey)).map(async (key) => {
            const value = await this.$apollo.query({
              query: GET_MEAN_VALUES_QUERY,
              variables: {
                key: key.queryKey,
                ...queryVariables,
              },
            });

            this.queryData = {
              ...this.queryData,
              [key.queryKey]: value.data.getMeanValues,
            };

            const latestValueResponse = await this.$apollo.query({
              query: GET_LATEST_VALUES_QUERY,
              variables: {
                key: key.queryKey,
                topic: queryVariables.topic,
                host: queryVariables.host,
              },
            });

            const latestValue = latestValueResponse.data.getLatestValues.length > 0 &&
              latestValueResponse.data.getLatestValues[latestValueResponse.data.getLatestValues.length - 1];

            this.latestData = {
              ...this.latestData,
              [key.queryKey]: latestValue,
            };
          }),
        );

        this.loaded = true;
      } catch (error) {
        console.log(error);
      }
    },
    endNow() {
      this.endDateTime = DateTime.now().toJSDate();
      this.loadAllValues();
    },
  },
};
</script>

<style lang="scss" scoped>
.mdAndUp {
  grid-template-columns: 2fr 2fr 2fr 1fr;
  >.parameter-select {
    grid-column: 1 / span 4;
  }
}

.smAndDown {
  grid-template-columns: 1fr;
}

.grid {
  display: grid;
  gap: 18px;
  width: 100%;
}

.empty-data {
  padding: 16px;
}

.pump-list {
  padding-left: 0;
  padding-right: 0;
}

.chart-controls {
  width: 100%;
}
</style>
